import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';
import { extractApiErrorMessage } from '../../../core/errors/error-message.util';

@Component({
    selector: 'app-login',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './login.html',
    styleUrl: './login.scss',
})
export class Login implements OnInit {
    authService = inject(AuthService);
    private router = inject(Router);

    step = signal<'email' | 'otp'>('email');
    email = signal<string>('');
    otp = signal<string>('');

    isLoading = signal<boolean>(false);
    errorMessage = signal<string>('');
    successMessage = signal<string>('');

    ngOnInit() {
        if (this.authService.isLoggedIn()) {
            this.authService.closeLoginModal();
        }
    }

    closeModal() {
        this.authService.closeLoginModal();
        this.resetState();
    }

    resetState() {
        this.step.set('email');
        this.email.set('');
        this.otp.set('');
        this.errorMessage.set('');
        this.successMessage.set('');
        this.isLoading.set(false);
    }

    onChangeEmail() {
        this.step.set('email');
        this.otp.set('');
        this.errorMessage.set('');
        this.successMessage.set('');
    }

    onSendCode() {
        const trimmedEmail = this.email().trim();
        if (!trimmedEmail) {
            this.errorMessage.set('Please enter a valid email address.');
            return;
        }

        this.isLoading.set(true);
        this.errorMessage.set('');
        this.successMessage.set('');

        this.authService.sendVerificationCode(trimmedEmail).subscribe({
            next: (res: any) => {
                if (res?.success === false) {
                    this.isLoading.set(false);
                    this.errorMessage.set(res.message || 'Failed to send verification code. Please try again.');
                    return;
                }
                this.isLoading.set(false);
                this.successMessage.set(res.message || 'Verification code has been sent to your email.');
                this.step.set('otp');
            },
            error: (err) => {
                this.isLoading.set(false);
                this.errorMessage.set(extractApiErrorMessage(err, 'Failed to send verification code. Please try again.'));
            }
        });
    }

    onVerifyCode() {
        const trimmedCode = this.otp().trim();
        const trimmedEmail = this.email().trim();

        if (!trimmedCode) {
            this.errorMessage.set('Please enter the verification code.');
            return;
        }

        this.isLoading.set(true);
        this.errorMessage.set('');

        this.authService.validateVerificationCode(trimmedEmail, trimmedCode).subscribe({
            next: (res: any) => {
                if (res?.success === false) {
                    this.isLoading.set(false);
                    this.errorMessage.set(res.message || 'Invalid verification code. Please try again.');
                    return;
                }
                this.isLoading.set(false);
                if (res.token) {
                    this.authService.saveSession(res.token, trimmedEmail);
                }
                this.closeModal();
                this.router.navigate(['/history']);
            },
            error: (err) => {
                this.isLoading.set(false);
                this.errorMessage.set(extractApiErrorMessage(err, 'Invalid verification code. Please try again.'));
            }
        });
    }
}
